import { DataQualityAggregatedTimeseries, DataQualityOverallScores } from '@lune-fe/dapi-models'
import { truncateDateToLocalDate, useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { formatNumbers, LoadingWrapper, MainLayoutContainer, Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import Stack from '@mui/material/Stack'
import moment from 'moment'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Range } from 'react-date-range'

import Divider from 'components/Divider'
import FiltersBox from 'components/FiltersBox'
import FiltersWithQueryParamsWrapper from 'components/FiltersWithQueryParamsWrapper'
import {
    getDataHeathImprovements,
    getDataQualityAggregatedTimeseries,
    getOverallDataHealthScores,
} from 'endpoints/dapi'
import useTitle from 'hooks/useTitle'
import { SnackbarMessages } from 'SnackbarMessages'
import DataHealthByModeTable from 'views/DataHealth/DataHealthByModeTable'
import DataHealthByShipmentLegsChart from 'views/DataHealth/DataHealthByShipmentLegsChart'
import Improvements, { ImprovementsProps } from 'views/DataHealth/Improvements'
import OverallScores from 'views/DataHealth/OverallScores'

export enum LegsStatus {
    SHIPPED = 'SHIPPED',
    NOT_SHIPPED = 'NOT_SHIPPED',
}

const getLabelForLegStatus = (status: LegsStatus) => {
    return status === LegsStatus.SHIPPED ? 'Shipment' : 'Pre-shipment'
}

const getStartEndDates = (dateRange: Range) => {
    const start = dateRange.startDate
        ? truncateDateToLocalDate(dateRange.startDate)
        : dateRange.startDate
    const end = dateRange.endDate ? truncateDateToLocalDate(dateRange.endDate) : dateRange.endDate
    return { start, end }
}

const getInterval = (dateRange: Range) => {
    const rangeDuration = moment.duration(moment(dateRange.endDate).diff(dateRange.startDate))
    return !dateRange.endDate || !dateRange.startDate || rangeDuration.asMonths() > 1
        ? 'month'
        : 'day'
}

const DataHealth = () => {
    useTitle('Data Health')
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const today = moment()

    const [dateRange, setDateRange] = useState<Range>({
        startDate: today.clone().subtract(3, `months`).toDate(),
        endDate: today.toDate(),
    })

    const from = useMemo(() => getStartEndDates(dateRange).start, [dateRange])
    const through = useMemo(() => getStartEndDates(dateRange).end, [dateRange])
    const interval = useMemo(() => getInterval(dateRange), [dateRange])

    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>()
    const [selectedStatuses, setSelectedStatuses] = useState<LegsStatus[]>([])
    const [queryParams, setQueryParams] = useState<string>()

    const [overallScores, setOverallScore] = useState<DataQualityOverallScores>()
    const [improvements, setImprovements] = useState<ImprovementsProps>()
    const [chartsData, setChartsData] = useState<DataQualityAggregatedTimeseries>()

    const [loadingOverallScores, setLoadingOverallScores] = useState<boolean>(true)
    const [loadingCharts, setLoadingCharts] = useState<boolean>(true)

    const loadOverallDataHealthScores = () => {
        getOverallDataHealthScores()
            .then((r) => {
                setOverallScore(r)
            })
            .catch((error) => {
                snackbar(SnackbarMessages.GENERIC_ERROR, { error })
            })
            .finally(() => {
                setLoadingOverallScores(false)
            })
    }
    const getImprovements = () => {
        getDataHeathImprovements()
            .then((r) => {
                setImprovements({
                    load: r.load,
                    mode: r.mode,
                    route: r.route,
                })
            })
            .catch((error) => {
                snackbar(SnackbarMessages.GENERIC_ERROR, { error })
            })
            .finally(() => {
                setLoadingOverallScores(false)
            })
    }

    const loadCharts = () => {
        getDataQualityAggregatedTimeseries({
            from,
            through,
            interval,
            accountIds: selectedAccountIds,
            isShipment:
                selectedStatuses.length === 0
                    ? undefined
                    : selectedStatuses[0] === LegsStatus.SHIPPED,
        })
            .then((r) => {
                setChartsData(r)
            })
            .catch((error) => {
                snackbar(SnackbarMessages.GENERIC_ERROR, { error })
            })
            .finally(() => {
                setLoadingCharts(false)
            })
    }

    useEffect(() => {
        loadOverallDataHealthScores()
        getImprovements()
    }, [])

    useEffect(() => {
        loadCharts()
    }, [queryParams, selectedAccountIds, selectedStatuses])

    return (
        <MainLayoutContainer headerComponent={<Text variant={'h4'}>Data Health</Text>}>
            <LoadingWrapper loading={loadingOverallScores} sx={{ height: '431px', width: '100%' }}>
                <Stack
                    direction="column"
                    sx={{
                        mb: 9,
                        width: '100%',
                    }}
                    spacing={{
                        xs: 9,
                    }}
                >
                    {overallScores && <OverallScores data={overallScores} />}
                    <Divider />
                    <FiltersWithQueryParamsWrapper<LegsStatus>
                        dropDownFilterName={'status'}
                        setDateRange={setDateRange}
                        dateRange={dateRange}
                        setSelectedAccountIds={setSelectedAccountIds}
                        selectedAccountIds={selectedAccountIds}
                        setSelectedTypes={setSelectedStatuses}
                        selectedTypes={selectedStatuses}
                        setQueryParams={setQueryParams}
                    />
                    {selectedAccountIds && (
                        <FiltersBox<LegsStatus>
                            preselectedDateRange={dateRange}
                            preselectedAccountIds={selectedAccountIds}
                            preselectedTypes={selectedStatuses}
                            setSelectedAccountIds={
                                setSelectedAccountIds as Dispatch<SetStateAction<string[]>>
                            }
                            setDateRange={setDateRange}
                            setSelectedTypes={setSelectedStatuses}
                            types={[LegsStatus.SHIPPED, LegsStatus.NOT_SHIPPED]}
                            transformDropDownLabel={(l) => {
                                return getLabelForLegStatus(l as LegsStatus)
                            }}
                            singleTypeSelect={true}
                            dropDownFilterName={'status'}
                        />
                    )}
                    {chartsData && (
                        <>
                            <Box>
                                <Text variant={'h5'}>Data quality by shipment legs</Text>
                                <Text variant={'body1'} sx={{ mt: 3 }}>
                                    {chartsData.byShipmentLegs.legs > 0
                                        ? `${formatNumbers(chartsData.byShipmentLegs.legs)} legs in ${formatNumbers(chartsData.byShipmentLegs.estimates!)} calculations`
                                        : `0 calculations`}
                                </Text>
                                <DataHealthByShipmentLegsChart
                                    data={chartsData.byShipmentLegs}
                                    loading={loadingCharts}
                                    dateRange={{ startDate: undefined, endDate: undefined }}
                                />
                            </Box>
                            <Box>
                                <Text variant={'h5'}>Data quality by mode</Text>
                                <DataHealthByModeTable
                                    loading={loadingCharts}
                                    data={chartsData.byMode}
                                    totalNumberOfLegs={chartsData.byShipmentLegs.legs}
                                />
                            </Box>
                        </>
                    )}
                    {improvements && (
                        <>
                            <Divider />
                            <Improvements
                                mode={improvements.mode}
                                load={improvements.load}
                                route={improvements.route}
                            />
                        </>
                    )}
                </Stack>
            </LoadingWrapper>
        </MainLayoutContainer>
    )
}

export default DataHealth
